import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-308b0382"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "container" }
const _hoisted_2 = ["data-left", "data-right", "data-expanded"]
const _hoisted_3 = ["aria-colcount", "aria-rowcount"]
const _hoisted_4 = { class: "thead" }
const _hoisted_5 = { class: "tr" }
const _hoisted_6 = ["aria-sort", "data-align", "data-divider"]
const _hoisted_7 = { class: "tbody" }
const _hoisted_8 = ["aria-rowindex", "aria-colindex", "data-align", "data-divider"]
const _hoisted_9 = { class: "controls" }
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Per page", -1))
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { key: 2 }

import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useResizeObserver, useScroll } from "@vueuse/core";
import { Col, Cols, Rows, Sort } from "./AppTable";
import AppTextbox from "./AppTextbox.vue";
import AppSelectMulti from "./AppSelectMulti.vue";
import AppSelectSingle from "./AppSelectSingle.vue";
import { Options } from "./AppSelectMulti";
import { Filters } from "@/api/facets";
import { closeToc } from "./TheTableOfContents";

interface Props {
  /** info for each column of table */
  cols: Cols;
  /** list of table rows, i.e. the table data */
  rows: Rows;
  /** sort key and direction */
  sort?: Sort;
  /** filters */
  availableFilters?: Filters;
  activeFilters?: Filters;
  /** items per page (two-way bound) */
  perPage?: number;
  /** starting item index (two-way bound) */
  start?: number;
  /** total number of items */
  total?: number;
  /** text being searched (two-way bound) */
  search?: string;
  /**
   * whether to show certain controls (temp solution, needed b/c this is a
   * controlled component and cannot paginate/search/etc on its own where needed
   * yet)
   */
  showControls?: boolean;
}

interface Emits {
  /** when sort changes */
  (event: "sort", sort: Sort): void;
  /** when filter changes */
  (event: "filter", colId: Col["id"], value: Options): void;
  /** when per page changes (two-way bound) */
  (event: "update:perPage", value: number): void;
  /** when start row changes (two-way bound) */
  (event: "update:start", row: number): void;
  /** when search changes (two-way bound) */
  (event: "update:search", value: string): void;
  /** when user requests download */
  (event: "download"): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppTable',
  props: {
    cols: null,
    rows: null,
    sort: { default: undefined },
    availableFilters: { default: undefined },
    activeFilters: { default: undefined },
    perPage: { default: 5 },
    start: { default: 0 },
    total: { default: 0 },
    search: { default: "" },
    showControls: { type: Boolean, default: true }
  },
  emits: ["sort", "filter", "update:perPage", "update:start", "update:search", "download"],
  setup(__props: any, { emit }: { emit: ({
  /** when sort changes */
  (event: "sort", sort: Sort): void;
  /** when filter changes */
  (event: "filter", colId: Col["id"], value: Options): void;
  /** when per page changes (two-way bound) */
  (event: "update:perPage", value: number): void;
  /** when start row changes (two-way bound) */
  (event: "update:start", row: number): void;
  /** when search changes (two-way bound) */
  (event: "update:search", value: string): void;
  /** when user requests download */
  (event: "download"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { cols: Cols, rows: Rows, sort: Sort, availableFilters: Filters, activeFilters: Filters, perPage: number, start: number, total: number, search: string, showControls: boolean }





/** whether table is expanded to be full width */
const expanded = ref(false);
/** table reference */
const table = ref<HTMLElement | null>(null);

/** table scroll state */
const { arrivedState } = useScroll(table, { offset: { left: 10, right: 10 } });

/** force table scroll to update */
async function updateScroll() {
  await nextTick();
  table.value?.dispatchEvent(new Event("scroll"));
}
onMounted(updateScroll);
watch(expanded, updateScroll);
useResizeObserver(table, updateScroll);

/** close table of contents when expanding */
watch(expanded, () => {
  if (expanded.value) closeToc();
});

/** when user clicks to first page */
function clickFirst() {
  emit("update:start", 0);
}

/** when user clicks to previous page */
function clickPrev() {
  emit("update:start", props.start - props.perPage);
}

/** when user clicks to next page */
function clickNext() {
  emit("update:start", props.start + props.perPage);
}

/** when user clicks to last page */
function clickLast() {
  emit("update:start", Math.floor(props.total / props.perPage) * props.perPage);
}

/** when user clicks a sort button */
function emitSort(col: Col) {
  let newSort: Sort;

  /** toggle sort direction */
  if (props.sort?.id === col.id) {
    if (props.sort?.direction === "down")
      newSort = { id: col.id, direction: "up" };
    else if (props.sort?.direction === "up") {
      newSort = null;
    } else {
      newSort = { id: col.id, direction: "down" };
    }
  } else {
    newSort = { id: col.id, direction: "down" };
  }

  emit("sort", newSort);
}

/** when user changes a filter */
function emitFilter(colId: Col["id"], value: Options) {
  emit("filter", colId, value);
}

/** when user changes rows per page */
function emitPerPage(value: string) {
  emit("update:perPage", Number(value));
  emit("update:start", 0);
}

/** when user types in search */
function emitSearch(value: string) {
  emit("update:search", value);
  emit("update:start", 0);
}

/** when user clicks download */
function emitDownload() {
  emit("download");
}

/** ending item index */
const end = computed((): number => props.start + props.rows.length);

/** grid column template widths */
const widths = computed((): string =>
  props.cols.map((col) => col.width || "auto").join(" ")
);

/** aria sort direction attribute */
const ariaSort = computed(() => {
  if (props.sort?.direction === "up") return "ascending";
  if (props.sort?.direction === "down") return "descending";
  return "none";
});

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_AppFlex, { direction: "col" }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          ref_key: "table",
          ref: table,
          class: "wrapper",
          "data-left": _unref(arrivedState).left,
          "data-right": _unref(arrivedState).right,
          "data-expanded": expanded.value
        }, [
          _createElementVNode("table", {
            class: "table",
            "aria-colcount": __props.cols.length,
            "aria-rowcount": __props.rows.length,
            style: _normalizeStyle({ gridTemplateColumns: _unref(widths) })
          }, [
            _createElementVNode("thead", _hoisted_4, [
              _createElementVNode("tr", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.cols, (col, colIndex) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: colIndex,
                    class: "th",
                    "aria-sort": _unref(ariaSort),
                    "data-align": col.align || 'left',
                    "data-divider": col.id === 'divider'
                  }, [
                    _createElementVNode("span", null, _toDisplayString(col.heading), 1),
                    (col.sortable)
                      ? _withDirectives((_openBlock(), _createBlock(_component_AppButton, {
                          key: 0,
                          icon: 
                    'arrow-' + (__props.sort?.id === col.id ? __props.sort?.direction : 'down')
                  ,
                          design: "small",
                          color: __props.sort?.id === col.id ? 'primary' : 'secondary',
                          style: _normalizeStyle({ opacity: __props.sort?.id === col.id ? 1 : 0.35 }),
                          onClick: _withModifiers(($event: any) => (emitSort(col)), ["stop"])
                        }, null, 8, ["icon", "color", "style", "onClick"])), [
                          [_directive_tooltip, 'Sort by ' + col.heading]
                        ])
                      : _createCommentVNode("", true),
                    (
                    __props.availableFilters &&
                    __props.activeFilters &&
                    __props.availableFilters[col.id] &&
                    __props.activeFilters[col.id] &&
                    __props.availableFilters[col.id]?.length
                  )
                      ? _withDirectives((_openBlock(), _createBlock(AppSelectMulti, {
                          key: 1,
                          name: 'Filter by ' + col.heading,
                          options: __props.availableFilters[col.id],
                          "model-value": __props.activeFilters[col.id],
                          design: "small",
                          onChange: (value) => emitFilter(col.id, value)
                        }, null, 8, ["name", "options", "model-value", "onChange"])), [
                          [_directive_tooltip, 'Filter by ' + col.heading]
                        ])
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_6))
                }), 128))
              ])
            ]),
            _createElementVNode("tbody", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.rows, (row, rowIndex) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: rowIndex,
                  class: "tr"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.cols, (col, colIndex) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: colIndex,
                      class: "td",
                      "aria-rowindex": rowIndex + 1,
                      "aria-colindex": colIndex + 1,
                      "data-align": col.align || 'left',
                      "data-divider": col.id === 'divider'
                    }, [
                      (_ctx.$slots[col.id])
                        ? _renderSlot(_ctx.$slots, col.id, {
                            key: 0,
                            row: row,
                            col: col,
                            cell: col.key ? row[col.key] : {}
                          })
                        : (col.key)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode(_toDisplayString(row[col.key]), 1)
                            ], 64))
                          : _createCommentVNode("", true)
                    ], 8, _hoisted_8))
                  }), 128))
                ]))
              }), 128))
            ])
          ], 12, _hoisted_3)
        ], 8, _hoisted_2),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", null, [
            (__props.showControls)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _hoisted_10,
                  _createVNode(AppSelectSingle, {
                    name: "Rows per page",
                    options: [
                { id: '5' },
                { id: '10' },
                { id: '20' },
                { id: '50' },
                { id: '100' },
                { id: '500' },
              ],
                    "model-value": { id: String(__props.perPage || 5) },
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = (value) => emitPerPage(value.id))
                  }, null, 8, ["model-value"])
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", null, [
            (__props.showControls)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: __props.start <= 0,
                    icon: "angle-double-left",
                    design: "small",
                    onClick: clickFirst
                  }, null, 8, ["disabled"]), [
                    [_directive_tooltip, 'Go to first page']
                  ]),
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: __props.start - __props.perPage < 0,
                    icon: "angle-left",
                    design: "small",
                    onClick: clickPrev
                  }, null, 8, ["disabled"]), [
                    [_directive_tooltip, 'Go to previous page']
                  ])
                ], 64))
              : _createCommentVNode("", true),
            (__props.total > 0)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (__props.showControls)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(__props.start + 1) + " — " + _toDisplayString(_unref(end)) + " of " + _toDisplayString(__props.total), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(__props.total) + " row(s)", 1))
                ], 64))
              : (_openBlock(), _createElementBlock("span", _hoisted_13, "no data")),
            (__props.showControls)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: __props.start + __props.perPage > __props.total,
                    icon: "angle-right",
                    design: "small",
                    onClick: clickNext
                  }, null, 8, ["disabled"]), [
                    [_directive_tooltip, 'Go to next page']
                  ]),
                  _withDirectives(_createVNode(_component_AppButton, {
                    disabled: __props.start + __props.perPage > __props.total,
                    icon: "angle-double-right",
                    design: "small",
                    onClick: clickLast
                  }, null, 8, ["disabled"]), [
                    [_directive_tooltip, 'Go to last page']
                  ])
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", null, [
            (__props.showControls)
              ? _withDirectives((_openBlock(), _createBlock(AppTextbox, {
                  key: 0,
                  class: "search",
                  icon: "search",
                  "model-value": __props.search,
                  onDebounce: emitSearch,
                  onChange: emitSearch
                }, null, 8, ["model-value"])), [
                  [_directive_tooltip, 'Search table data']
                ])
              : _createCommentVNode("", true),
            _withDirectives(_createVNode(_component_AppButton, {
              icon: "download",
              design: "small",
              onClick: emitDownload
            }, null, 512), [
              [_directive_tooltip, 'Download table data']
            ]),
            _withDirectives(_createVNode(_component_AppButton, {
              icon: expanded.value ? 'minimize' : 'maximize',
              design: "small",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (expanded.value = !expanded.value))
            }, null, 8, ["icon"]), [
              [_directive_tooltip, 
              expanded.value ? 'Collapse table' : 'Expand table to full width'
            ]
            ])
          ])
        ])
      ]),
      _: 3
    })
  ]))
}
}

})